import React, { useState, useEffect } from "react";
import { 
  Box, Typography, Card, TextField, Grid, Button, IconButton, 
  Stack, InputAdornment, MenuItem, Divider 
} from "@mui/material";
import { 
  X, Save, Receipt, User, Calendar, DollarSign, FileText, 
  Layers, CreditCard, UploadCloud 
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = { 
  ice: "#C2EDF0", 
  aqua: "#00C0CA", 
  teal: "#008B9C", 
  dark: "#22404D", 
  slate: "#94A3B8" 
};

const RUBROS = [
  "Papelería", "Locomoción", "Hospedaje", "Viáticos", "Fotografía", "Audio",
  "Comida y bebida", "Gastos varios", "Flete", "Locomoción Interdepartamental",
  "Materiales/Kit/Otros", "Manuales (compr./impr.)", "Diseño y armado",
  "Impresiones", "Publicidad", "Tarjeta de Celular", "Limpieza",
  "ANDE - Pasajes y locomoción", "ANDE - Viajes y viáticos DESEM",
  "ANDE - Eventos (audio, esc., locales)", "ANDE - Comunicación (video, difusión)",
  "ANDE - Pasajes, aloj., traslados Feria Regional",
  "ANDE - Mat para stand y difusión Feria Regional",
  "Otro (ingresar rubro en descripción)",
];

const FormularioCajaChica = ({ isModal, defaultPrograma, onSuccess }) => {
  const navigate = useNavigate();
  const [programas, setProgramas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [formValues, setFormValues] = useState({
    rubro: "", proveedor: "", comprobante: "", fecha: "",
    moneda: "", importe: "", descripcion: "", idPrograma: defaultPrograma || "",
    tipoGasto: "", file: null, centroCosto: "", nombrePrograma: "",
  });

  useEffect(() => {
    const fetchProgramas = async () => {
      try {
        const response = await fetch(`${apiUrl}/Programa/Get`, {
          headers: { apiKey: localStorage.getItem("apiKey") }
        });
        if (response.ok) {
          const data = await response.json();
          setProgramas(data);
          // Si hay un programa default (desde el Dashboard), cargar sus datos extra
          if (defaultPrograma) {
            const selected = data.find(p => p.id.toString() === defaultPrograma.toString());
            if (selected) {
              setFormValues(prev => ({ ...prev, nombrePrograma: selected.nombre, centroCosto: selected.centroCosto }));
            }
          }
        }
      } catch (error) { console.error(error); }
    };
    fetchProgramas();
  }, [defaultPrograma]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleProgramaChange = (e) => {
    const selectedId = e.target.value;
    const selected = programas.find(p => p.id.toString() === selectedId.toString());
    setFormValues(prev => ({
      ...prev,
      idPrograma: selectedId,
      nombrePrograma: selected ? selected.nombre : "",
      centroCosto: selected ? selected.centroCosto : "",
    }));
  };

  const handleFileChange = (e) => {
    setFormValues(prev => ({ ...prev, file: e.target.files[0] }));
  };

  const registrarGasto = async (e) => {
    e.preventDefault();
    if (formValues.importe <= 0) return toast.error("El monto debe ser mayor a 0");
    
    setCargando(true);
    const formData = new FormData();
    Object.keys(formValues).forEach(key => {
      if (key === 'file') {
        if (formValues.file) formData.append("File", formValues.file);
      } else {
        formData.append(key.charAt(0).toUpperCase() + key.slice(1), formValues[key]);
      }
    });
    formData.append("email", localStorage.getItem("email"));

    try {
      const res = await fetch(`${apiUrl}/Gasto`, {
        method: "POST",
        headers: { apiKey: localStorage.getItem("apiKey") },
        body: formData,
      });

      if (res.ok) {
        toast.success("Gasto registrado con éxito");
        if (isModal && onSuccess) {
          onSuccess();
        } else {
          navigate("/listadoGastosPorPrograma/" + formValues.idPrograma);
        }
      } else {
        toast.error("Error al registrar el gasto");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setCargando(false);
    }
  };

  return (
    <Box sx={{ 
      p: isModal ? 0 : { xs: 2, md: 4 }, 
      display: 'flex', justifyContent: 'center', 
      bgcolor: isModal ? 'transparent' : "#F8FAFB",
      minHeight: isModal ? 'auto' : "100vh" 
    }}>
      <Card elevation={0} sx={{ 
        p: { xs: 3, md: 4 }, 
        borderRadius: isModal ? '0px' : '32px', 
        border: isModal ? 'none' : '1px solid #eee', 
        width: '100%', maxWidth: 850, bgcolor: 'white' 
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, color: PALETTE.dark }}>Registrar Gasto</Typography>
            <Typography variant="caption" sx={{ color: PALETTE.slate, fontWeight: 600 }}>Caja chica y rendiciones</Typography>
          </Box>
          {!isModal && (
            <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: PALETTE.ice }}><X size={20} color={PALETTE.teal} /></IconButton>
          )}
        </Stack>

        <form onSubmit={registrarGasto}>
          <Grid container spacing={2}>
            {/* Programa y Rubro */}
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>PROGRAMA</Typography>
              <TextField select fullWidth name="idPrograma" value={formValues.idPrograma} onChange={handleProgramaChange} required sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: '#FAFAFA' } }}>
                {programas.map((p) => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>RUBRO</Typography>
              <TextField select fullWidth name="rubro" value={formValues.rubro} onChange={handleChange} required sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: '#FAFAFA' } }}>
                {RUBROS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Proveedor y Comprobante */}
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>PROVEEDOR</Typography>
              <TextField fullWidth name="proveedor" value={formValues.proveedor} onChange={handleChange} placeholder="Nombre" required InputProps={{ startAdornment: <InputAdornment position="start"><User size={16} /></InputAdornment> }} sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: '#FAFAFA' } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>N° COMPROBANTE</Typography>
              <TextField fullWidth name="comprobante" value={formValues.comprobante} onChange={handleChange} placeholder="001-002-..." required InputProps={{ startAdornment: <InputAdornment position="start"><Receipt size={16} /></InputAdornment> }} sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: '#FAFAFA' } }} />
            </Grid>

            {/* Fecha y Tipo de Gasto */}
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>FECHA</Typography>
              <TextField fullWidth type="date" name="fecha" value={formValues.fecha} onChange={handleChange} required sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: '#FAFAFA' } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>TIPO GASTO</Typography>
              <TextField select fullWidth name="tipoGasto" value={formValues.tipoGasto} onChange={handleChange} required sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: '#FAFAFA' } }}>
                <MenuItem value="Caja chica">Caja chica</MenuItem>
                <MenuItem value="Credito">Crédito</MenuItem>
              </TextField>
            </Grid>

            {/* Moneda e Importe */}
            <Grid item xs={12} md={4}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>MONEDA</Typography>
              <TextField select fullWidth name="moneda" value={formValues.moneda} onChange={handleChange} required sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: '#FAFAFA' } }}>
                <MenuItem value="$">Pesos ($)</MenuItem>
                <MenuItem value="USD">Dólares (USD)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>IMPORTE</Typography>
              <TextField fullWidth type="number" name="importe" value={formValues.importe} onChange={handleChange} required InputProps={{ startAdornment: <InputAdornment position="start"><DollarSign size={16} /></InputAdornment> }} sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: '#FAFAFA' } }} />
            </Grid>

            {/* Descripción */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>DESCRIPCIÓN</Typography>
              <TextField fullWidth name="descripcion" value={formValues.descripcion} onChange={handleChange} multiline rows={2} required sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: '#FAFAFA' } }} />
            </Grid>

            {/* Adjuntar Archivo */}
            {formValues.tipoGasto === "Caja chica" && (
              <Grid item xs={12}>
                <Button variant="outlined" component="label" fullWidth startIcon={<UploadCloud size={20} />} sx={{ borderRadius: '12px', py: 1.5, borderStyle: 'dashed', color: PALETTE.teal, borderColor: PALETTE.teal }}>
                  {formValues.file ? formValues.file.name : "Adjuntar Comprobante"}
                  <input type="file" hidden onChange={handleFileChange} />
                </Button>
              </Grid>
            )}
          </Grid>

          <Box mt={4}>
            <Button type="submit" fullWidth disabled={cargando} variant="contained" sx={{ borderRadius: '12px', py: 1.8, fontWeight: 800, bgcolor: PALETTE.teal, '&:hover': { bgcolor: PALETTE.dark } }}>
              {cargando ? "Registrando..." : "Registrar Gasto"}
            </Button>
          </Box>
        </form>
      </Card>
    </Box>
  );
};

export default FormularioCajaChica;